import type { ListBuilderConfiguration } from '../../types';
import { allTripsList } from './lists/all-trips';
import { completedTripsList } from './lists/completed-trips';
import { inProgressTripsList } from './lists/in-progress-trips';
import { pendingLoadTripsList } from './lists/pending-load-trips';
import { scheduledTripsList } from './lists/scheduled-trips';
import { todaysTripsList } from './lists/todays-trips';

/**
 * Trip list configuration.
 * Multiple views: All, Today's, Scheduled, In Progress, Completed.
 *
 * Sort defs (`tableSorts`) are referenced by each list's `defaultSortId`.
 * Without them the sort resolver logs "did not resolve to any sort
 * fields" on every list mount — the column id happens to match the sort
 * id by name but the resolver looks up `tableSorts` specifically.
 *
 * `objectTable: 'route'` here is a SQL alias the backend list service
 * uses; the underlying entity is TripEntity (table `trip` post-Phase-A).
 * `name` joins through `fk_route__id` to the Route row's name.
 */
export const TRIP_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		allTripsList,
		todaysTripsList,
		pendingLoadTripsList,
		scheduledTripsList,
		inProgressTripsList,
		completedTripsList,
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'routeSearch',
			'routeDateRange',
			'routeDriver',
			'routeVehicle',
			'routeStatus',
			'tripWarehouse',
		],
		// PD-13742: the Trips list defaults to the user's active warehouse but
		// stays a changeable chip (options = warehouses they can see). MY_WAREHOUSE
		// resolves server-side to the user's default; a user with no default sees
		// all warehouses. Config-level so it applies to every tab without
		// overriding the per-tab routeStatus filterRules.
		defaultFilters: {
			tripWarehouse: { id: 'MY_WAREHOUSE', label: 'My Warehouse' },
		},
	},
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'route' }],
			description: 'Sort trips by scheduled date, newest first',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [{ direction: 'ASC', fieldName: 'date', objectTable: 'route' }],
			description: 'Sort trips by scheduled date, oldest first',
		},
		{
			id: 'status-asc',
			name: 'Status (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'status', objectTable: 'route' },
				{ direction: 'ASC', fieldName: 'date', objectTable: 'route' },
			],
			description: 'Group trips by status, then by date',
		},
	],
	objects: [
		{
			// Post-Phase-A: this list's underlying table is `trip` (TripEntity);
			// detail page lives at /distribution/trip/:id (singular —
			// per 2026-05-26 URL rename; see module.config.ts route
			// declarations for the matching path block).
			baseUrl: '/distribution/trip/:id',
			objectLabel: 'Route',
			objectTable: 'route',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Route' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'driverId', fieldLabel: 'Driver ID' },
				{ fieldName: 'driverName', fieldLabel: 'Driver Name' },
				{ fieldName: 'vehicleId', fieldLabel: 'Vehicle ID' },
				{ fieldName: 'vehicleName', fieldLabel: 'Vehicle Name' },
				{ fieldName: 'warehouseId', fieldLabel: 'Warehouse ID' },
				{ fieldName: 'warehouseName', fieldLabel: 'Warehouse Name' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				// PD-13747 — pause/return-aware overlay for the Status column's
				// trip-status-badge cell.
				{ fieldName: 'activeTrip', fieldLabel: 'Status' },
				{ fieldName: 'totalStops', fieldLabel: 'Total Stops' },
				{ fieldName: 'completedStops', fieldLabel: 'Completed Stops' },
				{ fieldName: 'totalDistance', fieldLabel: 'Total Distance' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
			],
		},
	],
};
